import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { SkeletonCard } from '../components/common/SkeletonCard';
import { EmptyState } from '../components/common/EmptyState';
import { LumynBadge } from '../components/common/LumynBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  ArrowLeft, Save, Eye, Upload as UploadIcon, Trash2, Copy, BookOpen,
  Layers, ChevronDown, ChevronRight, Plus, HelpCircle, GripVertical,
  ArrowUp, ArrowDown, ImagePlus, X, Sparkles, Settings as SettingsIcon,
  Flame, Edit2, AlertTriangle, Loader2, Play, Film, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '../utils/uploadImage';
import AutoInsertQuizzesModal from '../components/quiz/AutoInsertQuizzesModal';
import QuizSettingsDialog from '../components/quiz/QuizSettingsDialog';
import InsertVideoDialog from '../components/video/InsertVideoDialog';
import { SectionVideoPills } from '../components/video/SectionVideoPills';
import { canonicalVideoUrl } from '../utils/videoUrl';
import { useAuth } from '../contexts/AuthContext';

export default function ModuleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, put, post, del } = useApi();
  const { hasRole } = useAuth();
  // Trainers can OPEN modules to preview slides + launch live sessions, but
  // every write action (Add/Edit/Delete section, Save, Publish, Archive,
  // Duplicate, Add video, Replace, Auto-insert quizzes, slide reorder,
  // thumbnail upload) is hidden client-side. Backend `require_editor` is
  // the source of truth — these guards just keep the UI honest.
  const canEdit = hasRole('editor');
  const [mod, setMod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [editForm, setEditForm] = useState({ title: '', description: '', category: '', tags: '', estimated_duration_minutes: '' });
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [autoInsertOpen, setAutoInsertOpen] = useState(false);
  const [quizSettingsId, setQuizSettingsId] = useState(null);
  const [insertVideoSectionId, setInsertVideoSectionId] = useState(null);
  // Slide being replaced — when the InsertVideoDialog closes after a
  // successful attach, we delete THIS one so the trainer never sees
  // "no video" mid-flow. Cleared on dialog dismiss without success.
  const [pendingReplaceSlide, setPendingReplaceSlide] = useState(null);
  const [resuming, setResuming] = useState(false);
  const fileInputRef = useRef(null);

  const loadModule = useCallback(async () => {
    try {
      const m = await get(`/modules/${id}`);
      setMod(m);
      setEditForm({
        title: m.title || '',
        description: m.description || '',
        category: m.category || '',
        tags: (m.tags || []).join(', '),
        // Show stored manual override only — leave blank for auto-estimated
        estimated_duration_minutes: m.duration_is_estimated ? '' : (m.estimated_duration_minutes ?? ''),
      });
      setThumbnailUrl(m.thumbnail_url || null);
    } catch (err) { console.error("Load failed:", err); } finally { setLoading(false); }
  }, [get, id]);

  useEffect(() => { loadModule(); }, [loadModule]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const trimmed = String(editForm.estimated_duration_minutes ?? '').trim();
      const duration = trimmed === '' ? null : Math.max(1, parseInt(trimmed, 10) || 0);
      await put(`/modules/${id}`, {
        title: editForm.title, description: editForm.description, category: editForm.category || null,
        tags: editForm.tags ? editForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        thumbnail_url: thumbnailUrl,
        estimated_duration_minutes: duration,  // null clears the manual override
      });
      toast.success('Module saved!');
      loadModule();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleThumbnailSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setThumbUploading(true);
    try {
      const res = await uploadImage(file, 'module_thumbnail');
      setThumbnailUrl(res.url);
      // Persist immediately so reloads and the module browser pick it up
      await put(`/modules/${id}`, { thumbnail_url: res.url });
      toast.success('Thumbnail updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Thumbnail upload failed');
    } finally {
      setThumbUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleThumbnailClear = async () => {
    setThumbUploading(true);
    try {
      await put(`/modules/${id}`, { thumbnail_url: null });
      setThumbnailUrl(null);
      toast.success('Custom thumbnail removed — first slide will be used');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear thumbnail');
    } finally {
      setThumbUploading(false);
    }
  };

  const handlePublish = async () => {
    try { await post(`/modules/${id}/publish`); toast.success('Module published!'); loadModule(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
  };

  const handleArchive = async () => {
    try { await post(`/modules/${id}/archive`); toast.success('Module archived'); loadModule(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Archive failed'); }
  };

  const handleDuplicate = async () => {
    try { const res = await post(`/modules/${id}/duplicate`); toast.success('Module duplicated!'); navigate(`/editor/modules/${res.module_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Duplicate failed'); }
  };

  const handleAddSection = async () => {
    try { await post(`/modules/${id}/sections`, { title: 'New Section' }); toast.success('Section added'); loadModule(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!confirm('Delete this section and all its content?')) return;
    try { await del(`/modules/${id}/sections/${sectionId}`); toast.success('Section deleted'); loadModule(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Phase 2 — launch a Live session from a section's quiz. Trainer presses
  // "Run live" → backend creates a session bound to this section + quiz →
  // we navigate to /editor/live?session=<id> for the host dashboard.
  const handleRunLive = async (section) => {
    if (!section?.quiz_id) {
      toast.error('Add a quiz to this section first');
      return;
    }
    try {
      const res = await post('/live/sessions', {
        quiz_id: section.quiz_id,
        mode: 'section_run',
        target_module_id: id,
        target_section_id: section.section_id,
      });
      toast.success(`Session ready · code ${res.ref_code}`);
      navigate(`/editor/live?session=${res.session_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not launch live session');
    }
  };

  const toggleSection = (sId) => setExpandedSections(p => ({ ...p, [sId]: !p[sId] }));

  const handleResumePlan = async () => {
    setResuming(true);
    try {
      const res = await post(`/modules/${id}/auto-quizzes/resume`, {});
      if (res?.ai_quizzes_created > 0) {
        toast.success(`Resumed: ${res.ai_quizzes_created} quizzes (${res.questions_created} questions) created.`);
      } else if ((res?.skipped || []).length > 0) {
        toast.warning('AI generation failed again for those sections. Try once more or author them manually.');
      } else {
        toast.info(res?.message || 'Nothing to resume');
      }
      loadModule();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resume failed');
    } finally {
      setResuming(false);
    }
  };

  if (loading) return <div className="space-y-4">{Array(3).fill(0).map((_, i) => <SkeletonCard key={`skel-${i}`} />)}</div>;
  if (!mod) return <EmptyState icon={BookOpen} title="Module not found" />;

  return (
    <div className="space-y-6 relative" data-testid="module-editor">
      <div aria-hidden className="page-blob bg-[var(--lumyn-content)]" />
      <div className="relative z-10 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/editor/modules')} data-testid="back-to-list" className="mt-1.5"><ArrowLeft className="w-4 h-4" /></Button>
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)] text-[11px] font-semibold uppercase tracking-[0.14em]">
              <BookOpen className="w-3.5 h-3.5" />Module
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight">{mod.title}</h1>
            <div className="flex items-center gap-2"><LumynBadge variant={mod.status || 'draft'} size="sm" /><span className="text-xs text-muted-foreground">v{mod.version}</span></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="save-btn"><Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save'}</Button>}
          <Button variant="outline" size="sm" asChild data-testid="preview-btn"><Link to={`/modules/${id}/learn`}><Eye className="w-4 h-4 mr-1" />Present</Link></Button>
          {canEdit && mod.status === 'draft' && <Button size="sm" onClick={handlePublish} data-testid="publish-btn"><UploadIcon className="w-4 h-4 mr-1" />Publish</Button>}
          {canEdit && mod.status === 'published' && <Button size="sm" variant="outline" onClick={handleArchive}>Archive</Button>}
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoInsertOpen(true)}
              data-testid="auto-insert-quizzes-btn"
              className="text-[var(--lumyn-quiz)] border-[var(--lumyn-quiz)]/40 hover:bg-[var(--lumyn-quiz)]/10 hover:text-[var(--lumyn-quiz)]"
            >
              <Sparkles className="w-4 h-4 mr-1" />Auto-insert quizzes
            </Button>
          )}
          {canEdit && <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="duplicate-btn"><Copy className="w-4 h-4 mr-1" />Duplicate</Button>}
        </div>
      </div>

      {/* Drop 3 — Resume CTA for AI failures from last plan run */}
      {(mod.last_plan_failures?.length || 0) > 0 && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-3"
          data-testid="resume-cta"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">
              {mod.last_plan_failures.length} section{mod.last_plan_failures.length === 1 ? '' : 's'} failed AI quiz generation last time.
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              Lumyn can retry just those sections without disturbing the ones that succeeded.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleResumePlan}
            disabled={resuming}
            data-testid="resume-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {resuming ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Resume
          </Button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Metadata */}
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-sm">Module Details</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Thumbnail</label>
              <div className="mt-1.5 relative rounded-lg border bg-muted/30 overflow-hidden aspect-video flex items-center justify-center group" data-testid="module-thumbnail-preview">
                {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt="Module thumbnail" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-xs text-muted-foreground px-3">
                    <BookOpen className="w-6 h-6 mx-auto mb-1.5 opacity-40" />
                    <p>Using first slide as thumbnail</p>
                  </div>
                )}
                {thumbUploading && (
                  <div className="absolute inset-0 bg-background/70 flex items-center justify-center text-xs">Uploading…</div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleThumbnailSelect}
                data-testid="module-thumbnail-input"
              />
              <div className="flex gap-2 mt-2">
                <Button type="button" variant="outline" size="sm" className="flex-1" disabled={thumbUploading}
                  onClick={() => fileInputRef.current?.click()} data-testid="module-thumbnail-upload-btn">
                  <ImagePlus className="w-3.5 h-3.5 mr-1" />{thumbnailUrl ? 'Replace' : 'Upload'}
                </Button>
                {thumbnailUrl && (
                  <Button type="button" variant="ghost" size="sm" disabled={thumbUploading}
                    onClick={handleThumbnailClear} data-testid="module-thumbnail-clear-btn">
                    <X className="w-3.5 h-3.5 mr-1" />Clear
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">PNG, JPG or WebP. Max 5 MB.</p>
            </div>
            <div><label className="text-xs text-muted-foreground">Title</label><Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} data-testid="edit-title" /></div>
            <div><label className="text-xs text-muted-foreground">Description</label><textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} className="w-full p-2.5 rounded-lg border bg-background text-sm h-24 resize-none" data-testid="edit-description" /></div>
            <div><label className="text-xs text-muted-foreground">Category</label><Input value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} data-testid="edit-category" /></div>
            <div><label className="text-xs text-muted-foreground">Tags (comma-separated)</label><Input value={editForm.tags} onChange={e => setEditForm(f => ({ ...f, tags: e.target.value }))} data-testid="edit-tags" /></div>
            <div>
              <label className="text-xs text-muted-foreground">Estimated duration (minutes)</label>
              <Input
                type="number"
                min="1"
                value={editForm.estimated_duration_minutes}
                onChange={e => setEditForm(f => ({ ...f, estimated_duration_minutes: e.target.value }))}
                placeholder={`Auto: ~${((mod.total_slides || 0) + (mod.total_quizzes || 0)) * 3} min`}
                data-testid="edit-duration"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Leave blank to auto-calculate at 3 min per slide / quiz section.
                {mod.duration_is_estimated && ' Currently auto-estimated.'}
              </p>
            </div>
            <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
              <p>Sections: {mod.total_sections}</p><p>Slides: {mod.total_slides}</p><p>Quizzes: {mod.total_quizzes}</p><p>Enrolled: {mod.enrolled_count}</p>
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2"><Layers className="w-4 h-4" />Sections ({mod.sections?.length || 0})</h2>
            {canEdit && <Button size="sm" onClick={handleAddSection} data-testid="add-section-btn"><Plus className="w-4 h-4 mr-1" />Add Section</Button>}
          </div>
          {(mod.sections || []).map((s, i) => (
            <div key={s.section_id} className="border rounded-lg overflow-hidden" data-testid="editor-section">
              <div className="flex items-center gap-2 p-3 bg-muted/30">
                <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                <button onClick={() => toggleSection(s.section_id)} className="flex items-center gap-1">
                  {expandedSections[s.section_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <span className="flex-1 text-sm font-medium">
                  {s.section_type === 'quiz' ? <span className="flex items-center gap-1.5"><HelpCircle className="w-3.5 h-3.5 text-[var(--lumyn-quiz)]" />{s.title}</span> : s.title}
                </span>
                <span className="text-xs text-muted-foreground">{s.total_slides || s.slides?.length || 0} slides</span>
                {/* Video pills — surfaces every video slide so trainers can see
                    + view + replace + delete without expanding the section. */}
                <SectionVideoPills
                  slides={s.slides}
                  moduleId={id}
                  onChanged={loadModule}
                  onReplace={canEdit ? ((slide) => {
                    setPendingReplaceSlide(slide);
                    setInsertVideoSectionId(s.section_id);
                  }) : null}
                />
                {s.section_type !== 'quiz' && (s.total_slides || s.slides?.length || 0) > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    data-testid={`present-from-section-${i}`}
                    className="text-[var(--lumyn-content)] border-[var(--lumyn-content)]/30 hover:bg-[var(--lumyn-content)]/10 hover:text-[var(--lumyn-content)]"
                  >
                    <Link to={`/modules/${id}/learn?from_section=${s.section_id}`}>
                      <Play className="w-3.5 h-3.5 mr-1" />Present from here
                    </Link>
                  </Button>
                )}
                {s.section_type === 'quiz' && s.quiz_id && (
                  <>
                    {canEdit && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/editor/modules/${id}/quizzes/${s.quiz_id}`)}
                        data-testid={`edit-questions-${i}`}
                        className="text-[var(--lumyn-quiz)] border-[var(--lumyn-quiz)]/30 hover:bg-[var(--lumyn-quiz)]/10 hover:text-[var(--lumyn-quiz)]"
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-1" />Edit questions
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRunLive(s)}
                      data-testid={`run-live-${i}`}
                      className="text-[var(--lumyn-live)] border-[var(--lumyn-live)]/40 hover:bg-[var(--lumyn-live)]/10 hover:text-[var(--lumyn-live)]"
                    >
                      <Flame className="w-3.5 h-3.5 mr-1" />Run live
                    </Button>
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => setQuizSettingsId(s.quiz_id)} data-testid={`quiz-settings-${i}`}>
                        <SettingsIcon className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </>
                )}
                {canEdit && s.section_type !== 'quiz' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setInsertVideoSectionId(s.section_id)}
                    data-testid={`add-video-${i}`}
                    title="Add a video to this section"
                    className="text-[var(--lumyn-content)] hover:bg-[var(--lumyn-content)]/10"
                  >
                    <Film className="w-3.5 h-3.5 mr-1" />Add video
                  </Button>
                )}
                {canEdit && <Button variant="ghost" size="sm" onClick={() => handleDeleteSection(s.section_id)} data-testid={`delete-section-${i}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>}
              </div>
              {expandedSections[s.section_id] && s.slides?.length > 0 && (
                <div className="p-3 space-y-2 border-t">
                  {s.slides.map((sl, j) => {
                    const isVideo = sl.media_type === 'video';
                    const thumb = isVideo ? sl.media_poster_url : sl.thumbnail_url;
                    return (
                      <div key={sl.slide_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 text-sm" data-testid={`slide-row-${j}`} data-media-type={sl.media_type || 'image'}>
                        <span className="w-6 text-center text-xs text-muted-foreground">{j + 1}</span>
                        {thumb
                          ? <div className="relative">
                              <img src={thumb} alt="" className="w-16 h-9 rounded object-cover bg-muted" />
                              {isVideo && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded">
                                  <Play className="w-3.5 h-3.5 text-white fill-white" />
                                </div>
                              )}
                            </div>
                          : <div className="w-16 h-9 rounded bg-muted flex items-center justify-center">
                              {isVideo ? <Film className="w-4 h-4 text-muted-foreground/60" /> : <BookOpen className="w-4 h-4 text-muted-foreground/40" />}
                            </div>}
                        <div className="flex-1 min-w-0">
                          <div className="truncate">
                            {sl.title || (isVideo ? 'Video' : `Slide ${j + 1}`)}
                            {isVideo && sl.media_source && sl.media_source !== 'upload' && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">({sl.media_source})</span>
                            )}
                          </div>
                          {/* Surface the actual URL for video slides so trainers
                              can see at a glance which video is attached. */}
                          {isVideo && (
                            <a
                              href={canonicalVideoUrl(sl) || '#'}
                              target="_blank"
                              rel="noreferrer"
                              className="block text-[11px] text-[var(--lumyn-content)] hover:underline truncate"
                              onClick={(e) => { if (!canonicalVideoUrl(sl)) e.preventDefault(); }}
                              data-testid={`slide-video-url-${j}`}
                            >
                              {canonicalVideoUrl(sl) || '(uploaded MP4)'}
                            </a>
                          )}
                        </div>
                        {canEdit && isVideo && (
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setPendingReplaceSlide(sl);
                                setInsertVideoSectionId(s.section_id);
                              }}
                              data-testid={`slide-replace-${j}`}
                              title="Replace this video"
                              className="h-7 px-2 text-[var(--lumyn-content)] hover:bg-[var(--lumyn-content)]/10"
                            >
                              <RefreshCw className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (!window.confirm('Delete this video from the section?')) return;
                                try {
                                  await del(`/modules/${id}/slides/${sl.slide_id}`);
                                  toast.success('Video deleted');
                                  loadModule();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Delete failed');
                                }
                              }}
                              data-testid={`slide-delete-${j}`}
                              title="Delete this video"
                              className="h-7 px-2 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          {(!mod.sections || mod.sections.length === 0) && <EmptyState icon={Layers} title="No sections" description="Add a section to start building content." />}
        </div>
      </div>

      <AutoInsertQuizzesModal
        open={autoInsertOpen}
        onClose={() => setAutoInsertOpen(false)}
        moduleId={id}
        sections={mod.sections || []}
        onDone={loadModule}
      />
      <QuizSettingsDialog
        open={!!quizSettingsId}
        onClose={() => { setQuizSettingsId(null); loadModule(); }}
        quizId={quizSettingsId}
        moduleEditorBackPath={`/editor/modules/${id}`}
      />
      <InsertVideoDialog
        open={!!insertVideoSectionId}
        onClose={() => {
          // Dismissed without a successful attach → cancel pending replace
          setInsertVideoSectionId(null);
          setPendingReplaceSlide(null);
        }}
        sectionId={insertVideoSectionId}
        // Surface the section's current slides so the dialog can show a
        // position picker. For the Replace flow, pre-select the OLD slide's
        // index so the new video lands in the same visual slot — the trailing
        // delete keeps the math balanced.
        existingSlides={
          (mod.sections || []).find((s) => s.section_id === insertVideoSectionId)?.slides || []
        }
        defaultPosition={pendingReplaceSlide ? pendingReplaceSlide.order : null}
        onAdded={async () => {
          // If this was a replace flow, delete the OLD slide now that the
          // new one is attached. Best-effort — if delete fails, the trainer
          // ends up with two video slides which they can clean up manually.
          if (pendingReplaceSlide) {
            try {
              await del(`/modules/${id}/slides/${pendingReplaceSlide.slide_id}`);
            } catch (e) {
              toast.error('New video added but old one could not be removed — please delete it manually.');
            }
            setPendingReplaceSlide(null);
          }
          await loadModule();
        }}
      />
      </div>
    </div>
  );
}
